import { useState } from "react";
import { ClipboardPaste, Layers, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useOperacie } from "@/api/hooks/useOperacie";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import { jePostup, postupPopis } from "@/lib/operacie";
import {
  useBulkDeleteDielce,
  useBulkUpdateDielce,
  type BulkVysledok,
} from "@/api/hooks/useBulkDielce";
import {
  hranaPodlaVzoru,
  resolveAutoEdge,
  STRANY_HRAN,
  type StranaHrany,
} from "@/lib/autoEdge";
import { useEdgeInfo } from "@/components/dielec/DielecVisual";
import { HranaPickerDialog } from "@/components/dielec/HranyEditor";
import { SlotEdgePickerDialog } from "@/components/dielec/SlotEdgePickerDialog";
import { ohlasUpozorneniaHranHromadne } from "@/lib/stavHran";
import {
  noveKesky,
  typNaExistujucichStranach,
  useSchrankaHran,
  vlozHranyNaDielce,
} from "@/lib/prenosHran";
import { cn } from "@/lib/utils";
import type {
  Dielec,
  DielecUpdate,
  HranaPodlaVzoru,
  MaterialSlot,
} from "@/api/schemas";

/** Hrúbky, ktoré ponúka rýchly prepínač hrán v detaile dielca. */
const HRUBKY = ["0.5", "1", "2"] as const;

/** Trojstav pre nadmieru — prázdne znamená „nemeniť". */
type NadmieraVolba = "" | "zapnut" | "vypnut";

/** Hrana pre jeden dielec — konkrétna, alebo dôvod, prečo žiadna. */
type HranaDielca = { id: string; nahradena: boolean } | { chyba: string };

/** Pripočítaj výsledok jedného volania k súhrnu celej akcie. */
function pripocitaj(v: BulkVysledok, r: BulkVysledok): void {
  v.hotovo += r.hotovo;
  v.chyby.push(...r.chyby);
  v.upozornenia = [...(v.upozornenia ?? []), ...(r.upozornenia ?? [])];
}

/** Hrúbka dosky dielca — bez nej rozhodne backend (18 mm). */
function zDielca(d: Dielec): number | null {
  return d.z_mm != null ? parseFloat(String(d.z_mm)) : null;
}

export interface HromadnaUpravaPanelProps {
  zakazkaId: string;
  /** Vybrané dielce (už bez operačných riadkov a vrstiev). */
  dielce: Dielec[];
  sloty: MaterialSlot[];
  /** Po úspešnej akcii — editor zruší výber. */
  onHotovo: () => void;
}

/**
 * Hromadná úprava viacerých dielcov naraz.
 *
 * Sedí v tom istom slote ako `DielecDetailPanel` a výšku berie **od obalu
 * pásu** (`h-full`) — inak by tabuľka nad ním poskakovala pri prechode
 * medzi jedným a viacerými vybranými dielcami. Vlastných 313 px tu bolo do
 * KOLA 63; dve nezávislé definície tej istej výšky sa rozišli o 2 px.
 *
 * Prázdne pole = **nemeniť**. Do PATCH-u ide len to, čo user nastavil, aby
 * hromadná zmena slotu nezmazala ručne vybraté hrany a naopak.
 */
export function HromadnaUpravaPanel({
  zakazkaId,
  dielce,
  sloty,
  onHotovo,
}: HromadnaUpravaPanelProps): JSX.Element {
  const [slotId, setSlotId] = useState("");
  const [operacia, setOperacia] = useState("");
  const [nadmiera, setNadmiera] = useState<NadmieraVolba>("");
  const [strany, setStrany] = useState<Set<StranaHrany>>(new Set());
  const [hrubka, setHrubka] = useState("");
  /**
   * Typ hrany (KOLO 169) — id hrany, ktorú user vybral ako vzor. User:
   * *„pri násobnom výbere daj mi možnosť zmeniť aj typ hrany, nie len
   * hrúbku"*. Každý dielec z neho dostane pásku v šírke pre svoju dosku.
   */
  const [typHrany, setTypHrany] = useState<string | null>(null);
  const [vyberTypu, setVyberTypu] = useState(false);
  const [bezi, setBezi] = useState(false);

  const bulkUpdate = useBulkUpdateDielce(zakazkaId);
  const bulkDelete = useBulkDeleteDielce(zakazkaId);
  // Postupy vrátane tých, čo si user založil sám (KOLO 108).
  const opQuery = useOperacie();
  const operacie = (opQuery.data?.data ?? []).filter(jePostup);
  // Projektová hrubá hrana — okno hrán slotu z nej ukazuje auto-voľbu
  const zakazkaQuery = useZakazkaDetail(zakazkaId);
  const hrubaHranaMm = zakazkaQuery.data?.data?.hruba_hrana_mm
    ? String(zakazkaQuery.data.data.hruba_hrana_mm)
    : undefined;
  const typInfo = useEdgeInfo({ id: typHrany, auto: false });
  // Hrany skopírované z iného dielca (KOLO 177) — vložia sa naraz všetkým
  const schranka = useSchrankaHran();

  const ids = dielce.map((d) => d.id);
  const kody = dielce.map((d) => d.kod).filter(Boolean) as string[];
  /** Aspoň jedno pole nastavené — inak nemá čo odoslať. */
  const maCoUlozit =
    !!slotId || !!operacia || !!nadmiera || strany.size > 0 || !!typHrany;

  function prepniStranu(s: StranaHrany): void {
    setStrany((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  /**
   * Masív po uložení — cieľový slot rozhoduje, inak doska dielca (KOLO 168).
   * Masív sa neolepuje, takže hrany z hromadnej úpravy preskočí.
   */
  function jeMasivPoUprave(d: Dielec): boolean {
    if (slotId) return !!sloty.find((s) => s.id === slotId)?.material?.masiv;
    return !!d.masiv;
  }

  /**
   * Riadok, ktorému hromadná úprava hrany nedá: masív a cenovka z hrany
   * (KOLO 169 — páska sama, hrany po obvode nemá; drží to aj server).
   */
  function bezStranPoUprave(d: Dielec): boolean {
    return jeMasivPoUprave(d) || !!d.riadok_z_hrany;
  }

  /**
   * Slot, z ktorého sa typ hrany vyberá: spoločný slot dielcov, ktoré sa
   * olepujú (alebo ten, ktorý im user práve nastavuje). Okno hrán slotu
   * ponúkne hrany zákazky a dekoru — pri zmiešaných slotoch by bolo
   * zavádzajúce, tam sa vyberá z celého cenníka hrán.
   */
  const slotyOlepovanych = new Set(
    dielce
      .filter((d) => !bezStranPoUprave(d))
      .map((d) => slotId || d.material_slot_id || ""),
  );
  const spolocnySlot =
    slotyOlepovanych.size === 1
      ? (sloty.find((s) => slotyOlepovanych.has(s.id)) ?? null)
      : null;
  const materialSpolocnehoSlotu =
    spolocnySlot?.material_id ?? spolocnySlot?.material?.id ?? null;

  function vyberTyp(id: string): void {
    setTypHrany(id);
    setVyberTypu(false);
  }

  /** Povedz, ktorým dielcom sa hrany nedali — inak by si user myslel opak. */
  function ohlasBezStran(preskocene: Dielec[]): void {
    const cenovky = preskocene.filter((d) => d.riadok_z_hrany);
    const masivne = preskocene.filter((d) => !d.riadok_z_hrany);
    const zoznam = (xs: Dielec[]) => xs.map((d) => d.kod ?? d.id).join(", ");
    if (masivne.length > 0) {
      toast({
        title: `Masív sa neolepuje (${masivne.length})`,
        description: `Hrany preskočené: ${zoznam(masivne)}.`,
      });
    }
    if (cenovky.length > 0) {
      toast({
        title: `Cenovka hrany po obvode nemá (${cenovky.length})`,
        description: `Hrany preskočené: ${zoznam(cenovky)}.`,
      });
    }
  }

  /**
   * Hlásenie výsledku — počet zmenených + menovite tie, čo neprešli,
   * a upozornenia na hrany z celej akcie jedným toastom (KOLO 169).
   */
  function ohlas(v: BulkVysledok, co: string): void {
    if (v.chyby.length === 0) {
      toast({ title: `${co} — ${v.hotovo} dielcov` });
    } else {
      const kodPodlaId = new Map(dielce.map((d) => [d.id, d.kod ?? d.id]));
      toast({
        variant: "destructive",
        title: `${co} — ${v.hotovo} OK, ${v.chyby.length} neprešlo`,
        description: v.chyby
          .map((c) => `${kodPodlaId.get(c.id) ?? c.id}: ${c.sprava}`)
          .join(" · "),
      });
    }
    // Až za výsledkom — toasty idú najnovší hore a vidno najviac tri
    ohlasUpozorneniaHranHromadne(v.upozornenia ?? []);
  }

  async function uloz(): Promise<void> {
    setBezi(true);
    try {
      const zaklad: DielecUpdate = {};
      if (slotId) (zaklad as Record<string, unknown>).material_slot_id = slotId;
      if (operacia) (zaklad as Record<string, unknown>).operacia = operacia;
      if (nadmiera) {
        (zaklad as Record<string, unknown>).nadmiera_aktivna =
          nadmiera === "zapnut";
      }

      if (strany.size === 0 && typHrany) {
        // Bez vybraných strán (KOLO 177): typ len tam, kde hrana je, hrúbka
        // každej strany ostane — čierna doska s dekorovou hranou naraz
        await ulozTypNaExistujucich(zaklad, typHrany);
        return;
      }
      if (strany.size > 0 && typHrany) {
        // Typ hrany: páska vybraného typu v šírke pre dosku každého dielca
        // — rodinu aj šírku rozhoduje backend (KOLO 169)
        await ulozPerDielec(zaklad, hranaPodlaTypu(typHrany), (kody) =>
          hrubka
            ? `Hrana ${hrubka} mm v potrebnej šírke nie je: ${kody.join(", ")}.`
            : `Hrúbka vybranej hrany v potrebnej šírke nie je: ${kody.join(", ")}.`,
        );
        return;
      }
      if (strany.size > 0 && hrubka) {
        // S konkrétnou hrúbkou sa musí riešiť per dielec — vybrané dielce
        // môžu mať rôzne sloty aj rôzne hrúbky dosky.
        await ulozPerDielec(
          zaklad,
          hranaPodlaHrubky,
          (kody) => `Hrana ${hrubka} mm nie je v dekore: ${kody.join(", ")}.`,
        );
        return;
      }

      const sHranami: DielecUpdate = { ...zaklad };
      if (strany.size > 0) {
        // Bez hrúbky stačí zapnúť auto — hranu si každému dielcu dopočíta
        // backend (`resolve_auto_edges`) z jeho vlastného slotu a hrúbky.
        for (const s of strany) {
          (sHranami as Record<string, unknown>)[`hrana_${s}_auto`] = true;
        }
      }
      // Masív a cenovka dostanú len zvyšok úpravy, hrany nie (KOLO 168, 169)
      const masivne = strany.size > 0 ? dielce.filter(bezStranPoUprave) : [];
      const masivIds = new Set(masivne.map((d) => d.id));
      const ostatne = ids.filter((id) => !masivIds.has(id));
      const v: BulkVysledok = { hotovo: 0, chyby: [] };
      for (const [cast, data] of [
        [ostatne, sHranami],
        [[...masivIds], zaklad],
      ] as [string[], DielecUpdate][]) {
        if (cast.length === 0 || Object.keys(data).length === 0) continue;
        pripocitaj(v, await bulkUpdate.mutateAsync({ ids: cast, data }));
      }
      ohlas(v, "Upravené");
      ohlasBezStran(masivne);
      if (v.chyby.length === 0) onHotovo();
    } finally {
      setBezi(false);
    }
  }

  /**
   * Typ hrany na stranách, ktoré hranu majú (KOLO 177). Dielec bez hrán
   * dostane len zvyšok úpravy — hranu mu typ nezapne.
   */
  async function ulozTypNaExistujucich(
    zaklad: DielecUpdate,
    vzorId: string,
  ): Promise<void> {
    const v: BulkVysledok = { hotovo: 0, chyby: [] };
    const k = noveKesky();
    const masivne: Dielec[] = [];
    for (const d of dielce) {
      let data: Record<string, unknown> = { ...zaklad };
      if (bezStranPoUprave(d)) {
        masivne.push(d);
      } else {
        let r: Awaited<ReturnType<typeof typNaExistujucichStranach>>;
        try {
          r = await typNaExistujucichStranach(d, vzorId, zDielca(d), k);
        } catch (err) {
          r = { chyba: extractErrorMessage(err) };
        }
        if (r && "chyba" in r) {
          v.chyby.push({ id: d.id, sprava: r.chyba });
          continue;
        }
        if (r) data = { ...data, ...r.data };
      }
      if (Object.keys(data).length === 0) continue;
      pripocitaj(v, await bulkUpdate.mutateAsync({ ids: [d.id], data: data as DielecUpdate }));
    }
    ohlas(v, "Typ hrany");
    ohlasBezStran(masivne);
    if (v.chyby.length === 0) onHotovo();
  }

  /** Hrany zo schránky na všetky vybrané dielce (KOLO 177). */
  async function vlozHrany(): Promise<void> {
    if (!schranka) return;
    setBezi(true);
    try {
      const v: BulkVysledok = { hotovo: 0, chyby: [] };
      const r = await vlozHranyNaDielce(dielce, schranka, {
        bezStran: bezStranPoUprave,
        uloz: async (d, data) =>
          pripocitaj(
            v,
            await bulkUpdate.mutateAsync({ ids: [d.id], data: data as DielecUpdate }),
          ),
      });
      v.chyby.push(...r.chyby);
      const masivne = r.preskocene;
      ohlas(v, `Hrany z ${schranka.kod ?? "dielca"}`);
      ohlasBezStran(masivne);
      if (v.chyby.length === 0) onHotovo();
    } finally {
      setBezi(false);
    }
  }

  /** Hrúbka hrany → konkrétna hrana z ponuky slotu dielca (`auto-edge`). */
  async function hranaPodlaHrubky(d: Dielec): Promise<HranaDielca> {
    const cielovySlot = slotId || d.material_slot_id;
    if (!cielovySlot) return { chyba: "bez materiálu zákazky" };
    const edge = await resolveAutoEdge(cielovySlot, zDielca(d), hrubka);
    if (!edge) return { chyba: `materiál neponúka hranu ${hrubka} mm` };
    return { id: edge.id, nahradena: edge.nahradena };
  }

  /**
   * Typ hrany → páska toho istého typu v šírke pre dosku dielca.
   *
   * Výsledok závisí len od hrúbky dosky, takže dielce rovnakej hrúbky
   * zdieľajú jeden dopyt — dvadsať 18 mm dielcov je jedna otázka.
   */
  function hranaPodlaTypu(vzorId: string): (d: Dielec) => Promise<HranaDielca> {
    const podlaHrubky = new Map<string, Promise<HranaPodlaVzoru>>();
    return async (d) => {
      const z = zDielca(d);
      const kluc = String(z ?? "");
      let dopyt = podlaHrubky.get(kluc);
      if (!dopyt) {
        dopyt = hranaPodlaVzoru(vzorId, z, hrubka || undefined);
        podlaHrubky.set(kluc, dopyt);
      }
      const r = await dopyt;
      if (!r.edge) {
        return { chyba: r.sprava ?? "typ hrany sa v potrebnej šírke nevyrába" };
      }
      return { id: r.edge.id, nahradena: r.nahradena };
    };
  }

  /**
   * Hrany s konkrétnou hranou sa riešia per dielec — vybrané dielce môžu
   * mať rôzne sloty aj hrúbky dosky. `najdi` preloží voľbu usera (hrúbka
   * alebo typ hrany) na hranu jedného dielca.
   */
  async function ulozPerDielec(
    zaklad: DielecUpdate,
    najdi: (d: Dielec) => Promise<HranaDielca>,
    popisNahrady: (kody: string[]) => string,
  ): Promise<void> {
    const v: BulkVysledok = { hotovo: 0, chyby: [] };
    // Dekory bez žiadanej hrúbky dostanú najbližšiu vyššiu (KOLO 103).
    // Toast per dielec by pri hromadnej úprave zaplavil obrazovku, takže
    // sa len spočítajú a pripíšu k výslednej hláške — tichá náhrada by
    // znamenala, že user nevie, čo reálne priradil.
    const nahradene: string[] = [];
    const masivne: Dielec[] = [];
    for (const d of dielce) {
      let data: DielecUpdate = zaklad;
      if (bezStranPoUprave(d)) {
        // Masív sa neolepuje (KOLO 168), cenovka je páska sama (KOLO 169)
        // — hrany preskočí, zvyšok uloží
        masivne.push(d);
        if (Object.keys(zaklad).length === 0) continue;
      } else {
        let hrana: HranaDielca;
        try {
          hrana = await najdi(d);
        } catch (err) {
          hrana = { chyba: extractErrorMessage(err) };
        }
        if ("chyba" in hrana) {
          v.chyby.push({ id: d.id, sprava: hrana.chyba });
          continue;
        }
        const sHranou: Record<string, unknown> = { ...zaklad };
        for (const s of strany) {
          sHranou[`hrana_${s}_id`] = hrana.id;
          // Ručný výber — auto-resolve ho nesmie prepísať
          sHranou[`hrana_${s}_auto`] = false;
        }
        data = sHranou as DielecUpdate;
        if (hrana.nahradena) nahradene.push(d.kod ?? d.id);
      }
      pripocitaj(v, await bulkUpdate.mutateAsync({ ids: [d.id], data }));
    }
    ohlas(v, "Upravené");
    ohlasBezStran(masivne);
    if (nahradene.length > 0) {
      toast({
        title: `Použitá najbližšia hrúbka (${nahradene.length})`,
        description: popisNahrady(nahradene),
      });
    }
    if (v.chyby.length === 0) onHotovo();
  }

  async function zmaz(): Promise<void> {
    if (
      !window.confirm(
        `Zmazať ${ids.length} ${ids.length === 1 ? "dielec" : "dielcov"}?`,
      )
    ) {
      return;
    }
    setBezi(true);
    try {
      const v = await bulkDelete.mutateAsync(ids);
      ohlas(v, "Zmazané");
      if (v.chyby.length === 0) onHotovo();
    } finally {
      setBezi(false);
    }
  }

  return (
    <div
      className="flex h-full flex-col overflow-hidden rounded-lg border bg-card"
      data-testid="hromadna-uprava-panel"
    >
      <div className="flex shrink-0 items-center gap-3 border-b bg-background-alt/40 px-4 py-1.5">
        <Layers className="h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-5">
            {ids.length} {ids.length === 1 ? "dielec" : "dielcov"} vybraných
          </div>
          <div
            className="truncate font-mono text-[10px] leading-4 text-muted-foreground"
            title={kody.join(", ")}
          >
            {kody.join(", ")}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto shrink-0 hover:border-destructive hover:bg-destructive hover:text-destructive-foreground"
          disabled={bezi}
          onClick={() => void zmaz()}
          title="Zmazať všetky vybrané dielce (pýta si potvrdenie)"
        >
          <Trash2 className="h-4 w-4" />
          Zmazať {ids.length}
        </Button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <p className="text-[11px] text-muted-foreground">
          Prázdne pole = nemeniť. Uloží sa len to, čo tu nastavíš.
        </p>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="space-y-1">
            <Label
              htmlFor="hu-slot"
              className="text-[10px] uppercase text-muted-foreground"
            >
              Materiál
            </Label>
            <select
              id="hu-slot"
              value={slotId}
              onChange={(e) => setSlotId(e.target.value)}
              className="h-8 w-full rounded-md border bg-background px-2 text-xs"
            >
              <option value="">— nemeniť —</option>
              {sloty.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.slot_kod}
                  {s.material ? ` · ${s.material.nazov}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">
              Hrany
            </Label>
            <div className="flex items-center gap-1">
              {STRANY_HRAN.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => prepniStranu(s)}
                  aria-pressed={strany.has(s)}
                  aria-label={`Hrana ${s.toUpperCase()}`}
                  title={`Zapnúť hranu ${s.toUpperCase()} na vybraných dielcoch`}
                  className={cn(
                    "h-8 w-9 rounded-md border font-mono text-[11px]",
                    strany.has(s)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "bg-background hover:bg-accent",
                  )}
                >
                  {s.toUpperCase()}
                </button>
              ))}
              <select
                value={hrubka}
                onChange={(e) => setHrubka(e.target.value)}
                disabled={strany.size === 0}
                aria-label="Hrúbka hrany"
                title={
                  typHrany
                    ? "Bez hrúbky dostane každý dielec hrúbku vybranej hrany"
                    : "Bez hrúbky sa zapne auto — hranu vyberie appka podľa materiálu a hrúbky dielca"
                }
                className="h-8 flex-1 rounded-md border bg-background px-2 text-xs disabled:opacity-50"
              >
                <option value="">{typHrany ? "ako typ" : "auto"}</option>
                {HRUBKY.map((h) => (
                  <option key={h} value={h}>
                    {h} mm
                  </option>
                ))}
              </select>
            </div>
            {/* Typ hrany (KOLO 169) — nielen hrúbka. Šírku dostane každý
                dielec podľa svojej dosky, preto štítok ukazuje vybranú
                hranu len ako vzor typu. */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setVyberTypu(true)}
                aria-label={typHrany ? `Typ hrany: ${typInfo.label}` : "Typ hrany"}
                title={
                  typHrany
                    ? "Typ hrany — šírku dostane každý dielec podľa hrúbky svojej dosky"
                    : "Vybrať typ hrany (dekor) — aj z celého cenníka"
                }
                className={cn(
                  "h-8 min-w-0 flex-1 truncate rounded-md border px-2 text-left text-xs disabled:opacity-50",
                  typHrany
                    ? "border-primary bg-background font-mono"
                    : "bg-background text-muted-foreground hover:bg-accent",
                )}
              >
                {typHrany ? typInfo.label : "Typ hrany…"}
              </button>
              {typHrany ? (
                <button
                  type="button"
                  onClick={() => setTypHrany(null)}
                  aria-label="Zrušiť typ hrany"
                  title="Zrušiť typ hrany — hranu určí hrúbka alebo auto"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background hover:bg-accent"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            {typHrany && strany.size === 0 ? (
              <p className="text-[10px] leading-4 text-muted-foreground">
                Bez vybraných strán: typ sa zmení len tam, kde hrana je,
                hrúbka každej strany ostane.
              </p>
            ) : null}
            {schranka ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-full justify-start text-xs"
                disabled={bezi}
                onClick={() => void vlozHrany()}
                title="Rovnaké strany a typ hrany ako skopírovaný dielec — šírku dostane každý podľa svojej dosky"
              >
                <ClipboardPaste className="h-3.5 w-3.5" />
                Vložiť hrany z {schranka.kod ?? "dielca"}
              </Button>
            ) : null}
          </div>

          <div className="space-y-1">
            <Label
              htmlFor="hu-operacia"
              className="text-[10px] uppercase text-muted-foreground"
            >
              Výrobný postup
            </Label>
            {/* Výber, nie voľný text (KOLO 108) — napísaný text sa dovtedy
                uložil ako `nazov_volny`, teda ako názov dielca, a to naraz
                všetkým vybraným. */}
            <select
              id="hu-operacia"
              value={operacia}
              onChange={(e) => setOperacia(e.target.value)}
              className="h-8 w-full rounded border border-input bg-background px-2 text-xs"
            >
              <option value="">nemeniť</option>
              {operacie.map((o) => (
                <option key={o.kod} value={o.kod}>
                  {postupPopis(o)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <Label
              htmlFor="hu-nadmiera"
              className="text-[10px] uppercase text-muted-foreground"
            >
              Nadmiera
            </Label>
            <select
              id="hu-nadmiera"
              value={nadmiera}
              onChange={(e) => setNadmiera(e.target.value as NadmieraVolba)}
              className="h-8 w-full rounded-md border bg-background px-2 text-xs"
            >
              <option value="">— nemeniť —</option>
              <option value="zapnut">zapnúť</option>
              <option value="vypnut">vypnúť</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t px-4 py-2">
        <Button size="sm" variant="outline" onClick={onHotovo} disabled={bezi}>
          Zrušiť výber
        </Button>
        <Button
          size="sm"
          disabled={!maCoUlozit || bezi}
          onClick={() => void uloz()}
          title={
            maCoUlozit
              ? `Použiť na ${ids.length} dielcov`
              : "Najprv nastav aspoň jedno pole"
          }
        >
          {bezi ? "Ukladám…" : `Použiť na ${ids.length}`}
        </Button>
      </div>

      {/* Okno sa montuje až pri otvorení — inak by sa kandidáti hrán
          načítavali pri každom hromadnom výbere, aj keď typ nikto nemení */}
      {vyberTypu && spolocnySlot && materialSpolocnehoSlotu ? (
        <SlotEdgePickerDialog
          open
          slotMaterialId={materialSpolocnehoSlotu}
          slotId={spolocnySlot.id}
          hrubaHranaMm={hrubaHranaMm}
          variantaId={spolocnySlot.varianta_id}
          currentId={typHrany}
          onClose={() => setVyberTypu(false)}
          onSelect={vyberTyp}
        />
      ) : vyberTypu ? (
        <HranaPickerDialog
          open
          currentId={typHrany}
          onClose={() => setVyberTypu(false)}
          onSelect={vyberTyp}
        />
      ) : null}
    </div>
  );
}
